import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
const baseUrl = process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:5000';
// ✅ Async action to update user profile
export const updateUserProfile = createAsyncThunk(
  'user/updateUserProfile',
  async ({ id, userData, profilePic }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = state.auth.token;

      if (!token) throw new Error('Authorization token is required');

      const formData = new FormData();
      Object.keys(userData).forEach((key) => {
        if (userData[key] !== undefined && userData[key] !== null) {
          formData.append(key, userData[key]);
        }
      });

      if (profilePic) {
        formData.append('profilePic', profilePic);
      }

      const response = await axios.put(
        `${baseUrl}/users/update/${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data.user;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

// ✅ Async action to delete user account
export const deleteUserAccount = createAsyncThunk(
  'user/deleteUserAccount',
  async (userId, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;

      if (!token) throw new Error('Authorization token is missing');

      const res = await axios.delete(`${baseUrl}/users/hard-delete/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 200) {
        return true; // signal success
      } else {
        return rejectWithValue('Failed to delete account');
      }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Error deleting account');
    }
  }
);

// ✅ Initial state
const initialState = {
  user: {
    name: '',
    email: '',
    contact: '',
    username: '',
    profilePic: '',
    role: '',
    village: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    dob: '',
    gender: '',
  },
  loading: false,
  error: null,
  deleteStatus: null, // NEW: optional status flag for delete
};

// ✅ Slice
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    resetUserProfile: (state) => {
      state.user = initialState.user;
      state.loading = false;
      state.error = null;
      state.deleteStatus = null;
    },
    setUserField: (state, action) => {
      const { field, value } = action.payload;
      state.user[field] = value;
    },
  },
  extraReducers: (builder) => {
    builder
      // Update Profile
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Account
      .addCase(deleteUserAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.deleteStatus = null;
      })
      .addCase(deleteUserAccount.fulfilled, (state) => {
        state.loading = false;
        state.user = initialState.user;
        state.deleteStatus = 'success';
      })
      .addCase(deleteUserAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.deleteStatus = 'failed';
      });
  },
});

export const { resetUserProfile, setUserField } = userSlice.actions;
export default userSlice.reducer;
